import { ButtonIcon } from '../../../../../../components/inputs/button-icon/button-icon';
import FontBody2 from '../../../../../../components/fonts/font-body2/font-body2';
import { FONT_COLOR, FONT_VARIANT } from '../../../../../../components/fonts/font-props';
import IconPlus from '../../../../../../components/icons/icon-plus/icon-plus';
import IconMinusRounded from '../../../../../../components/icons/icon-minus-rounded/icon-minus-rounded';
import { Tooltip } from '../../../../../../components/tooltip/tooltip';
import { TOOLTIP_PLACEMENTS } from '../../../../../../components/tooltip/tooltip-box';

import TooltipContent from './tooltip-content';

interface IFeatureActionsProps {
    featureId: number;
    isBeta: boolean;
    isActive: boolean;
    hasQuota: boolean;
    isActivatable: boolean;
    source?: string;
    onActivateFeature?: (featureId: number) => void;
    onDeactivateFeature?: (featureId: number) => void;
    isActivating: boolean;
    activatingFeatureId: number | null;
    isDeactivating: boolean;
    deactivatingFeatureId: number | null;
}

const FeatureActions: React.FC<IFeatureActionsProps> = ({
    featureId,
    isBeta,
    isActive,
    hasQuota,
    isActivatable,
    source,
    onActivateFeature,
    onDeactivateFeature,
    isActivating,
    activatingFeatureId,
    isDeactivating,
    deactivatingFeatureId
}) => {
    // Show deactivate button for active features that are "Direct" or "Plan + Direct"
    if (isActive && onDeactivateFeature && (source === 'Direct' || source === 'Plan + Direct')) {
        const isDisabled = isDeactivating; // Allow deactivating even if not activatable
        const tooltipText = !isActivatable 
            ? 'This feature must be deactivated manually'
            : source === 'Plan + Direct' 
                ? 'Remove from Direct (will remain in Plan)'
                : 'Deactivate feature';

        return (
            <div className="feature-actions">
                <div data-testid="deactivate-feature-button">
                    <Tooltip 
                        content={<TooltipContent content={tooltipText} />} 
                        placement={TOOLTIP_PLACEMENTS.TOP}
                    >
                        <ButtonIcon
                            Component={IconMinusRounded}
                            onClick={() => onDeactivateFeature(featureId)}
                            disabled={isDisabled}
                        />
                    </Tooltip>
                </div>
                {isDeactivating && deactivatingFeatureId === featureId && (
                    <div className="deactivating-indicator">
                        <FontBody2 variant={FONT_VARIANT.DEFAULT} color={FONT_COLOR.NEUTRAL10}>
                            Deactivating...
                        </FontBody2>
                    </div>
                )}
            </div>
        );
    }

    // Show activate button for inactive features without quota (only if activatable)
    if (!isActive && !hasQuota && onActivateFeature && isActivatable) {
        const isDisabled = isActivating;
        const tooltipText = 'Activate feature';

        return (
            <div className="feature-actions">
                <div data-testid="activate-feature-button">
                    <Tooltip 
                        content={<TooltipContent content={tooltipText} />} 
                        placement={TOOLTIP_PLACEMENTS.TOP}
                    >
                        <ButtonIcon
                            Component={IconPlus}
                            onClick={() => onActivateFeature(featureId)}
                            disabled={isDisabled}
                        />
                    </Tooltip>
                </div>
                {isActivating && activatingFeatureId === featureId && (
                    <div className="activating-indicator">
                        <FontBody2 variant={FONT_VARIANT.DEFAULT} color={FONT_COLOR.NEUTRAL10}>
                            Activating...
                        </FontBody2>
                    </div>
                )}
            </div>
        );
    }

    return null;
};

export default FeatureActions;
